import { defineNuxtPlugin, useRouter, useRuntimeConfig } from "#imports";
import type { RouteLocationNormalized, Router } from "#vue-router";
import type { ModuleOptions } from "../module";

/**
 * Get the Matomo tracker instance from the window object.
 *
 * @returns - The Matomo tracker instance
 */
const getMatomo = () => {
    return window.Matomo?.getAsyncTracker();
};

/**
 * Get the full URL of a route.
 *
 * @param router - The router instance
 * @param path - The path of the route
 * @returns - The full URL of the route
 */
const getFullUrl = (router: Router, path?: string) => {
    if (!path) return;

    return `${window.location.origin}${router.resolve(path).href}`;
};

/**
 * Some environments may pass boolean values as strings. This function
 * will parse the value and return a boolean.
 *
 * @param value - The value to parse
 * @returns - The boolean interpretation of the value
 */
const parseStringOrBoolean = (value: string | boolean | undefined) => {
    if (typeof value === "string") {
        return value === "true";
    }

    return value;
};

/**
 * Add a script tag to the document head asynchronously.
 *
 * @param src - The source URL of the script to load
 * @param crossOrigin - The cross-origin attribute of the script
 * @returns - A promise that resolves when the script has been loaded
 */
const loadScript = (src: string, crossOrigin?: string): Promise<void> => {
    const script = document.createElement("script");

    script.src = src;
    script.async = true;
    script.defer = true;

    if (crossOrigin && ['anonymous', 'use-credentials'].includes(crossOrigin)) {
        script.crossOrigin = crossOrigin;
    }

    const head = document.head || document.getElementsByTagName("head")[0];
    head.appendChild(script);

    return new Promise<void>((resolve, reject) => {
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    });
};

/**
 * Check if the Matomo tracker has been loaded. This function will
 * poll the window object until the tracker is available or until
 * the timeout has been reached.
 *
 * @param interval - Time in milliseconds to wait between checks
 * @param timeout - Time in milliseconds to wait before throwing an error
 */
const waitForMatomo = async (
    interval: number = 50,
    timeout: number = 5000,
): Promise<void> => {
    const startTime = Date.now();

    while (!window.Matomo) {
        if (Date.now() >= startTime + timeout) {
            throw new Error("[Matomo] Tracker script has not been loaded");
        }

        await new Promise((resolve) => setTimeout(resolve, interval));
    }
};

/**
 * Track a page view with the Matomo tracker.
 *
 * @param nuxt - The nuxt app
 * @param options - The module options
 * @param router - The router instance
 * @param to - The route to navigate to
 * @param from - The route to navigate from
 */
const trackPageView = (
    nuxt: NuxtApp,
    options: ModuleOptions,
    router: Router,
    to: RouteLocationNormalized,
    from?: RouteLocationNormalized,
) => {
    const matomo = getMatomo();

    if (matomo) {
        const referrerUrl = getFullUrl(router, from?.fullPath);
        const url = getFullUrl(router, to.fullPath);
        const {
            name
        }: any = nuxt.$client.page;

        const {
            authenticated,
            user
        }: any = nuxt.$authentication;

        matomo.enableJSErrorTracking();

        if (referrerUrl) {
            matomo.setReferrerUrl(referrerUrl);
        }

        if (url) {
            matomo.setCustomUrl(url);
        }

        if (name) {
            matomo.setDocumentTitle(name);
        }

        if (authenticated) {
            matomo.setUserId(user.customerId);
        }

        if (options.debug) {
            console.debug(`[Matomo] Tracking page view for ${url}`);
        }

        matomo.trackPageView();

        if (options.enableLinkTracking) {
            matomo.enableLinkTracking();
        }
    }
};
// https://github.com/openmost/nuxt-matomo
/**
 * Define the Matomo plugin. This plugin will load the Matomo tracker
 * script and configure the tracker with the provided options.
 * 
 * @param nuxt - The nuxt app
 */
export default defineNuxtPlugin(async (nuxt: NuxtApp) => {
    console.log('[Matomo]: Init.');
    const config: any = await useApi('/setting/matomo');
    const router = useRouter();
    const options = { ...((useRuntimeConfig().public.matomo as ModuleOptions) || {}), ...config };

    const isDebug = parseStringOrBoolean(options.debug);
    const isEnabled = parseStringOrBoolean(options.enabled);

    if (!isEnabled) {
        if (isDebug) {
            console.debug("[Matomo] Plugin has been disabled");
        }

        return;
    }

    if (isDebug) {
        console.debug("[Matomo] Plugin has been enabled");
    }

    if (((!options.cloudId && !options.host) || !options.siteId) && isEnabled) {
        console.error(
            "[Matomo] Cloud ID, Host or SiteId is not defined. Please provide a host and/or siteId in the module options.",
        );

        return;
    }

    let matomoHost = options.cloudId
        ? `https://${options.cloudId}`
        : options.host
    const matomoContainerId = options.containerId

    const trackerScript = `${matomoHost}/${options.trackerFileName}`;
    const trackerEndpoint = `${matomoHost}/matomo.php`;
    let crossorigin: '' | 'anonymous' | 'use-credentials' = '';

    if (options.crossOrigin && ['anonymous', 'use-credentials'].includes(options.crossOrigin)) {
        crossorigin = options.crossOrigin as '' | 'anonymous' | 'use-credentials';
    }

    if (matomoHost.includes('matomo.cloud') && matomoContainerId) {
        matomoHost = `https://cdn.matomo.cloud/${new URL(matomoHost).hostname}`;

        // Generate container URL
        const url = `${matomoHost}/container_${matomoContainerId}.js`

        // Default Matomo Tag Manager snippet
        const _mtm = window._mtm = window._mtm || [];
        _mtm.push({ 'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start' });

        await loadScript(url, crossorigin);

        if (isDebug) {
            console.debug(`[Matomo] Container script "${matomoContainerId}" has been loaded.`);
        }
    }

    window._paq = window._paq || [];
    window._paq.push(['setTrackerUrl', trackerEndpoint]);
    window._paq.push(['setSiteId', options.siteId as number]);

    if (options.enableLinkTracking) {
        window._paq.push(['enableLinkTracking']);
    }

    if (options.disableCookies) {
        window._paq.push(['disableCookies']);
    } else {
        window._paq.push(['requireCookieConsent']);
    }

    if (options.enableHeartBeatTimer) {
        window._paq.push([
            'enableHeartBeatTimer',
            options.heartBeatTimerInterval as number,
        ]);
    }

    try {
        await loadScript(trackerScript, crossorigin);
        await waitForMatomo(options.scriptInterval, options.scriptTimeout);
        
        if (isDebug) {
            console.debug("[Matomo] Tracker script has been loaded.");
        }

        window.addEventListener('consent-studio__updated__granted__analytics', function (event) {
            if (isDebug) {
                console.debug("[Matomo] Consent Studio: User granted analytics.");
            }
        });

        window.addEventListener('consent-studio__updated__denied__analytics', function (event: any) {
            if (isDebug) {
                console.debug('[Matomo] Consent Studio: User denied analytics.');
            }
        });

        router.afterEach((to: any, from: any) => {

            const matomo = getMatomo();

            if (matomo) {
                const {
                    path,
                    article,
                    viewName
                } = nuxt.$client.page;

                switch (viewName) {
                    case 'category-articles':
                        matomo.setEcommerceView(
                            false,
                            false,
                            path);
                        break;
                    case 'article-overview':
                        // No action. See the product tracking further down.
                        break;
                    default:
                }

                trackPageView(nuxt, options, router, to, from);

                // Tracking product views.
                useMtmPush({
                    event: 'productView',
                    products: article?.products.map((product: any) => ({
                        id: product.quantity === 1
                            ? product.productCode
                            : `${product.productCode}-MPX${product.quantity}`,
                        name: product.productName,
                        category: path.substring(0, path.lastIndexOf('/'))
                    }))
                });
            }
        });
    } catch (error) {
        console.warn("[Matomo] Tracker script not loaded:"  );
    }
});